import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { Client, Contract } from '@shared/types';
import { contractService } from '../../services/contractService';
import { useClientContacts } from '../../hooks/useClientContacts';
import { useTemplates } from '../../hooks/useTemplates';
import { RichTextEditor } from '../RichTextEditor';

interface BulkContractEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  // Selected contracts in order. Each one's generated document gets attached;
  // each one's status transitions internal_review → client_review on send.
  contracts: Contract[];
  onSent: () => void;
}

// Reasonable default body composer. Lists each contract by title with a
// short description (template type if known). The user can freely edit
// before sending — once they do, this auto-rewrite stops.
function buildDefaultBody(client: Client, contracts: Contract[]): string {
  const greeting = `<p>Hi,</p>`;
  const intro =
    contracts.length === 1
      ? `<p>Please find attached the following document for <strong>${client.name}</strong>'s review:</p>`
      : `<p>Please find attached the following documents for <strong>${client.name}</strong>'s review:</p>`;
  const items = contracts
    .map((c) => {
      const title = c.title || c.clientName || 'Contract';
      const type = c.templateType ?? '';
      const descBits = [type, c.executiveSummary?.slice(0, 120)].filter(Boolean).join(' — ');
      const desc = descBits ? `: ${descBits}` : '';
      return `<li><strong>${title}</strong>${desc}</li>`;
    })
    .join('');
  const list = `<ul>${items}</ul>`;
  const closing = `<p>Tracked changes have been enabled — please redline as needed and return at your convenience. Let me know if you have any questions.</p><p>Thanks,</p>`;
  return greeting + intro + list + closing;
}

function buildDefaultSubject(client: Client, contracts: Contract[]): string {
  if (contracts.length === 1) {
    return `${contracts[0].title || 'Contract'} for review — ${client.name}`;
  }
  return `Contracts for review — ${client.name}`;
}

export function BulkContractEmailModal({
  isOpen,
  onClose,
  client,
  contracts,
  onSent,
}: BulkContractEmailModalProps) {
  const { contacts, loading: contactsLoading } = useClientContacts(client.id);
  const { templates } = useTemplates();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [includeW9, setIncludeW9] = useState(false);
  const [selectedW9Id, setSelectedW9Id] = useState('');

  const lastDefaultSubjectRef = useRef('');
  const lastDefaultBodyRef = useRef('');

  const contactsWithEmail = useMemo(
    () => contacts.filter((c) => !!c.email),
    [contacts],
  );

  const w9Templates = useMemo(
    () => templates.filter((t) => t.templateType === 'W9' && t.isActive),
    [templates],
  );

  // Reset on open. Defaults reflect the contracts that were passed in.
  useEffect(() => {
    if (!isOpen) return;
    const initialSubject = buildDefaultSubject(client, contracts);
    const initialBody = buildDefaultBody(client, contracts);
    setSubject(initialSubject);
    setBody(initialBody);
    lastDefaultSubjectRef.current = initialSubject;
    lastDefaultBodyRef.current = initialBody;
    setSelectedIds(new Set());
    setIncludeW9(false);
    setSelectedW9Id('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, client.id, contracts.map((c) => c.id).join(',')]);

  // Auto-select the W-9 template when there's exactly one.
  useEffect(() => {
    if (!includeW9) return;
    if (w9Templates.length === 1 && !selectedW9Id) {
      setSelectedW9Id(w9Templates[0].id);
    }
  }, [includeW9, w9Templates, selectedW9Id]);

  const toggleRecipient = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one recipient.');
      return;
    }
    if (includeW9 && !selectedW9Id) {
      toast.error('Pick which W-9 to attach.');
      return;
    }
    setSending(true);
    const toastId = toast.loading(
      `Sending ${contracts.length} contract${contracts.length === 1 ? '' : 's'}…`,
    );
    try {
      const result = await contractService.emailForClientReview({
        contractIds: contracts.map((c) => c.id),
        toContactIds: Array.from(selectedIds),
        subject,
        bodyHtml: body,
        additionalTemplateIds: includeW9 && selectedW9Id ? [selectedW9Id] : [],
      });
      const names = result.recipients.map((r) => r.name).join(', ');
      toast.success(
        `Sent ${result.attachments.length} attachment${result.attachments.length === 1 ? '' : 's'} to ${names}`,
        { id: toastId },
      );
      onSent();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to send contracts',
        { id: toastId },
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={sending ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Send {contracts.length} Contract{contracts.length === 1 ? '' : 's'} for Client Review
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    disabled={sending}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-6 py-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                    {contactsLoading ? (
                      <p className="text-sm text-gray-500">Loading contacts…</p>
                    ) : contactsWithEmail.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No contacts with an email address for {client.name}. Add one on the client first.
                      </p>
                    ) : (
                      <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-44 overflow-y-auto">
                        {contactsWithEmail.map((c) => (
                          <label
                            key={c.id}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(c.id)}
                              onChange={() => toggleRecipient(c.id)}
                              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900">
                                {c.name}
                                <span className="ml-2 text-xs font-normal text-gray-500">
                                  · {c.contactType}
                                </span>
                              </p>
                              <p className="truncate text-xs text-gray-500">{c.email}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <RichTextEditor value={body} onChange={setBody} minHeight={200} />
                    <p className="mt-1 text-xs text-gray-500">
                      Your saved email signature is appended automatically. Track Changes is enabled on each outgoing .docx — the SharePoint canonical copies stay clean.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Attachments
                    </label>
                    <div className="space-y-2">
                      {contracts.map((c) => {
                        const fileName =
                          (c.metadata as any)?.generated_document?.file_name ??
                          (c.title ? `${c.title}.docx` : `${c.id}.docx`);
                        return (
                          <div
                            key={c.id}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700"
                          >
                            <PaperClipIcon className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{fileName}</span>
                          </div>
                        );
                      })}

                      {w9Templates.length > 0 && (
                        <div className="px-3 py-2 border border-gray-200 rounded-md">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={includeW9}
                              onChange={(e) => setIncludeW9(e.target.checked)}
                              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                            />
                            <PaperClipIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">Include W-9 form</span>
                          </label>
                          {includeW9 && w9Templates.length > 1 && (
                            <select
                              value={selectedW9Id}
                              onChange={(e) => setSelectedW9Id(e.target.value)}
                              className="mt-2 ml-7 w-[calc(100%-1.75rem)] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="">Select a W-9 form…</option>
                              {w9Templates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          )}
                          {includeW9 && w9Templates.length === 1 && (
                            <p className="mt-1 ml-7 text-xs text-gray-500">
                              {w9Templates[0].name}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3">
                  <button
                    onClick={onClose}
                    disabled={sending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={
                      sending ||
                      selectedIds.size === 0 ||
                      contactsWithEmail.length === 0
                    }
                    className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
